/**
 * Core numeric constants for the colony sim, transcribed verbatim from
 * colony-game-poc-spec.md. Do not derive or "clean up" any of these —
 * they are cited from the spec section noted in each comment.
 */

// --- §2 Core constants -----------------------------------------------------

/** 1 real second per tick. spec §2 */
export const TICK_MS = 1000 as const;

/** Target session length: 10,800 ticks ≈ 7.5 game-days (3 real hours). spec §2 */
export const TARGET_SESSION_TICKS = 10800 as const;

/** City grid is 25 x 25 plots. spec §2 */
export const CITY_GRID_SIZE = 25 as const;

/** World has 2 rings of hexes around the home hex. spec §2 */
export const WORLD_RINGS = 2 as const;

/** 18 minable hexes (home hex is pre-depleted). spec §2, §6 rule 4 */
export const MINABLE_HEX_COUNT = 18 as const;

/** Hex side length in metres (area 64.95 km^2). spec §2 */
export const HEX_SIDE_M = 5000 as const;

/** Hex center-to-center spacing in metres (across-flats width). spec §2 */
export const HEX_CENTER_SPACING_M = 8660 as const;

// --- §3 Coordinate systems --------------------------------------------------

/** Factory block occupies plots 11..13 on each axis (inclusive, lower bound). spec §3 */
export const FACTORY_MIN = 11 as const;

/** Factory block occupies plots 11..13 on each axis (inclusive, upper bound). spec §3 */
export const FACTORY_MAX = 13 as const;

/** 625 total plots minus the 3x3 factory block leaves 616 buildable plots. spec §3 */
export const BUILDABLE_PLOTS = 616 as const;

// --- §4.2 Atmospheric intake -------------------------------------------------

/** Energy cost per unit of atmospheric N2 drawn. spec §4.2 */
export const ATMOSPHERIC_ENERGY_PER_UNIT = 4 as const;

/** Each unit of atmospheric intake yields 2 N. spec §4.2 */
export const ATMOSPHERIC_YIELD_N = 2 as const;

// --- §5 Energy ---------------------------------------------------------------

/**
 * Power the main factory makes for itself, from its solar collectors.
 *
 * Spec §5 calls this "factory base output" and gives no source. It is the roof:
 * the lander arrived with collectors, and they are sized to bootstrap — enough
 * to run the refinery and light the first few buildings, not enough to carry a
 * city. Everything past that is power plants, which is what makes the first one
 * a real milestone rather than a formality.
 */
export const FACTORY_SOLAR_OUTPUT = 200 as const;

// --- §6 World generation -----------------------------------------------------

/**
 * Total mineral units generated across the area. REPLACES spec §6's
 * 250,000-320,000. See SPEC-ERRATA.md E17.
 *
 * §6 derives its figure backwards from what the city consumes — about 150,000
 * mineral units for a full grid, times 1.7-2.1 for slack. That reasoning never
 * checks the number against how fast the material can be EXTRACTED, and the
 * Phase 8 harness showed the gap is enormous: at §7's own endgame drone
 * throughput the entire world empties in under 500 ticks of a 10,800-tick
 * session, leaving the mining half of the game over before the city is a tenth
 * built.
 *
 * The 19 hexes are an AREA of a planet, chosen before the colony launched — not
 * a whole world — so how much is in the ground is a free parameter, set here to
 * make a session work rather than inherited from the grid's shopping list.
 *
 * Derived from the two rates that actually bound a session:
 *
 *  - REFINING is the city's real constraint. Dissociation runs at 48/tick base,
 *    rising with factory districts; a session processes on the order of a
 *    million units. Only about 150,000 of that becomes city — strip mining
 *    hauls sulfur, chlorine and fluorine home too, and no recipe consumes them.
 *
 *  - MINING at a sensible fleet size runs a few hundred units per tick. Much
 *    more than that only fills a stockpile the factory cannot process, so a
 *    player who over-invests in drones is buying nothing.
 *
 * ~2 million units keeps both loops alive for most of three hours: the ground
 * visibly empties as the session runs rather than in its first few minutes, and
 * depletion starts to bite near the end, which is where §6 wants the pressure.
 */
export const WORLD_SUPPLY_MIN = 1800000 as const;
export const WORLD_SUPPLY_MAX = 2600000 as const;

/**
 * Fraction of the home hex recovered as starting inventory, in basis points.
 *
 * Spec §6 rule 4 makes the whole home hex the starting stock. That was fine when
 * a hex held ~15,000 units; at the supply above it would hand the player more
 * material than the entire city needs, before the first tick.
 *
 * 25% is what the landing scavenged off the surface — enough to bootstrap the
 * factory for roughly the first thousand ticks, not enough to finish anything.
 * The rest of the hex is spent: the site reads as fully depleted either way.
 */
export const STARTING_SCAVENGE_BP = 2500 as const;

// --- §9 Build queue / mechs ---------------------------------------------------

/** Base build time in ticks at 1 assigned mech. spec §9 */
export const BASE_BUILD_TICKS = 30 as const;

/** Mechs available at game start. spec §9 */
export const STARTING_MECHS = 2 as const;

/** Cost to build one additional mech, in goods units. spec §9 */
export const MECH_COST = {
  steelBeam: 60,
  wiring: 20,
} as const;

/** Per-plot Chebyshev-distance build time penalty multiplier. spec §9 */
export const BUILD_DISTANCE_PENALTY_PER_PLOT = 0.02 as const;

// --- §7 Drone cycle timing ----------------------------------------------------
//
// Phase durations are stored in DECI-TICKS (tenths of a tick), not whole ticks.
//
// Spec §7 works in fractional ticks: "17.32 km / 5 = 3.5 ticks travel, plus 2
// ticks mine and 1 tick unload = 6.5 ticks", and derives ~23 mineral-units per
// game-minute from three minis on ring 1. Rounding each leg to a whole tick
// gives a 7-tick cycle and 21.4 units/min, which misses the spec's own figure.
// Deci-ticks keep the arithmetic in integers (spec §11 rule 2) while landing on
// a 6.4-tick cycle and 23.4 units/min.
//
// Phase overshoot is carried into the next phase, so cycle length stays exact
// over many trips rather than losing up to 0.9 ticks per transition.

/** Deci-ticks per tick. Every stored duration is in these units. */
export const DECITICKS_PER_TICK = 10 as const;

/** Time to fill a drone at the deposit. spec §7: 2 ticks. */
export const MINING_DECITICKS = 20 as const;

/** Time to unload at the factory. spec §7: 1 tick. */
export const UNLOAD_DECITICKS = 10 as const;

// --- §4.2, §5 Factory energy ---------------------------------------------------

/**
 * Energy to dissociate one mineral unit into its elements.
 *
 * ASSIGNED, not specced. §5 lists demand as "synthesis + intake + building
 * upkeep" and never prices dissociation, but §13 item 6 asks whether refining
 * is rate-capped "or purely energy-limited" and answers "currently
 * energy-limited" — which is only true if it draws energy. See SPEC-ERRATA.md E9.
 *
 * At 2/unit the ~150,000 mineral units a session consumes cost ~300,000 energy,
 * around 14% of what base power alone supplies over 10,800 ticks. First knob to
 * turn in Phase 8 if power reads as too tight.
 */
export const DISSOCIATION_ENERGY_PER_UNIT = 2 as const;

/**
 * Atmospheric intake units per tick at density 1.0. ASSIGNED, not specced —
 * §4.2 defines `ratePerTick` as scaling with density but gives no base.
 *
 * 20 units/tick yields 40 N and costs 80 energy: 40% of base power, so running
 * the intake is a real decision rather than free background income.
 */
export const BASE_ATMOSPHERIC_INTAKE_PER_TICK = 20 as const;

/**
 * Stop drawing nitrogen once this much is held. See SPEC-ERRATA E20.
 *
 * Fertilizer needs potassium as well as nitrogen, and potassium comes from
 * sylvite, which the landing scavenge does not include — so for the first few
 * hundred ticks the colony CANNOT make fertilizer whatever it does. The intake
 * ran regardless and cost 80 energy a tick, 40% of the factory solar output,
 * to build an 18,000-unit nitrogen pile with no consumer. It throttled the
 * whole factory to 77% and slowed every other component down.
 *
 * At 48 N a tick — two per fertilizer against the synthesis budget — this
 * reserve is roughly 40 ticks of uninterrupted fertilizer production, so the
 * intake still runs well ahead of demand rather than hand-to-mouth.
 */
export const ATMOSPHERIC_N_RESERVE = 2000 as const;

// --- Factory throughput (Phase 4 rebuild) --------------------------------------
//
// Spec §13 item 6 asks whether the factory is rate-capped "or purely
// energy-limited", and leaves it energy-limited. The Phase 8 harness showed that
// does not work, so both stages are now rate-capped. See SPEC-ERRATA.md E14.
//
// The sizing target is the whole grid inside one session: 616 plots is roughly
// 125,000 goods over 10,800 ticks, about 11.6 goods/tick sustained, which needs
// on the order of 30 mineral units/tick behind it. The base rates sit above that
// so a competent run is never hard-blocked, and factory districts raise both so
// the ceiling is something the player builds their way past.

/** Goods the factory can synthesise per tick, across all recipes. */
export const SYNTHESIS_THROUGHPUT_BASE = 24 as const;

/** Manufacturing output per +1 good/tick. One district (250) adds 5. */
export const MANUFACTURING_PER_SYNTHESIS_UNIT = 50 as const;

/** Mineral units the factory can dissociate per tick. */
export const DISSOCIATION_THROUGHPUT_BASE = 48 as const;

/** Manufacturing output per +1 mineral/tick. One district (250) adds 10. */
export const MANUFACTURING_PER_DISSOCIATION_UNIT = 25 as const;

// --- Phase 10: colonists ------------------------------------------------------

/**
 * How long food keeps, in ticks. One game-day.
 *
 * Food spoils so that it cannot be banked. Without expiry a player builds farms
 * early, accumulates a million meals against a population of zero, and the
 * colonist layer has no supply problem left to solve. It is also what gives the
 * building on/off switch its purpose: running a farm before anyone is there to
 * eat is a pure waste of power.
 */
export const FOOD_SHELF_LIFE_TICKS = 1440 as const;

/**
 * Buckets the food store is divided into.
 *
 * Tracking a cohort per tick would be exact and would put 1,440 integers into
 * state and the determinism hash. Eight buckets covering the shelf life between
 * them costs eight, and the granularity — spoilage arriving in eighths rather
 * than continuously — is invisible at the volumes food is produced in.
 */
export const FOOD_BUCKETS = 8 as const;

/**
 * Food eaten per 1,000 colonists per tick.
 *
 * Sized against a full city: roughly 250 habitations at 400 living space each is
 * about 100,000 colonists, and roughly 90 farms at 200 food/tick is about
 * 18,000 food/tick. 200 per 1,000 puts consumption at 20,000 — slightly beyond
 * what that mix supplies, so feeding a full city takes deliberate farm building
 * rather than happening by accident.
 */
export const FOOD_PER_1000_COLONISTS = 200 as const;

/** Colonists delivered by one arrival request. */
export const ARRIVAL_BATCH = 250 as const;

/** Happiness weights, in basis points. Sum to 10,000. */
export const HAPPINESS_WEIGHT_FOOD = 3500 as const;
export const HAPPINESS_WEIGHT_WATER = 2500 as const;
export const HAPPINESS_WEIGHT_HOUSING = 2500 as const;
export const HAPPINESS_WEIGHT_AMENITY = 1500 as const;

/** Above this, a colony with spare housing grows. */
export const BREEDING_HAPPINESS_BP = 6500 as const;

/** Below this, colonists start leaving. */
export const EMIGRATION_HAPPINESS_BP = 3500 as const;

/**
 * Growth and loss per 10,000 population per tick.
 *
 * Deliberately slow. At 20 per 10,000 a content colony doubles in roughly 3,500
 * ticks — visible across a session without turning population into a number that
 * scrolls. Emigration is faster than growth, because a colony failing should
 * empty out noticeably rather than decline imperceptibly.
 */
export const BREEDING_RATE_PER_10K = 20 as const;
export const EMIGRATION_RATE_PER_10K = 60 as const;

// --- Phase 11: market ---------------------------------------------------------

/**
 * Units the freighters can move per tick, across all commodities together.
 *
 * A rate limit rather than a free-for-all, for the same reason the factory has
 * one: without it a stockpile becomes an instant pile of credits and the market
 * is a button rather than a decision.
 *
 * 240 is where a sweep stopped paying. Measured on silicon-starved worlds, the
 * mean city grew by 8.2 plots going from 60 to 240 — and by 0.2 more going to
 * 720, and 0.6 more going to 2000. Past this point the freighter is not the
 * constraint; INCOME is, since credits come from waste sold at scrap and no
 * amount of hold space raises that.
 *
 * It also happens to be the level at which selling keeps pace with waste
 * production, which is the market'''s actual day job: strip mining delivers 35 to
 * 80 units a tick of sulfur, chlorine and fluorine that nothing consumes, and at
 * 60 the stockpile grew anyway.
 */
export const MARKET_THROUGHPUT = 240 as const;

/**
 * Units of one-way trade that would move a price by 100%.
 *
 * Market impact. Dumping sulfur drives sulfur down; buying silicon drives
 * silicon up. This is what stops "sell everything" from being free money, and it
 * makes trading steadily more profitable than trading in bursts.
 */
export const MARKET_DEPTH = 24000 as const;

/**
 * How fast a price returns toward base, in basis points per tick.
 *
 * Mean reversion. Without it, prices ratchet permanently and an early dump ruins
 * a commodity for the rest of the session. At 15/tick a fully crashed price
 * recovers over a few thousand ticks — slow enough to hurt, not so slow that it
 * is unrecoverable.
 */
export const PRICE_REVERSION_BP = 15 as const;

/** Prices are clamped to this band, as a multiple of base in basis points. */
export const MIN_PRICE_BP = 2500 as const;
export const MAX_PRICE_BP = 40000 as const;

/**
 * Buying costs this multiple of the selling price.
 *
 * The spread. Without it a player could push a price down by selling, buy back
 * cheaply, and profit from their own market impact — a money printer that has
 * nothing to do with running a colony.
 */
export const BUY_SPREAD_BP = 13000 as const;

/**
 * Scales derived base prices. Chosen so the most abundant element lands near 1
 * credit and the scarcest in the tens, which keeps credit totals readable
 * without needing thousands separators everywhere.
 */
export const PRICE_SCALE = 400 as const;

/**
 * Drones the colony lands with. SPEC-ERRATA E23.
 *
 * The spec provides three minis at 50 units and 5 km/tick. Four standards at
 * 200 and 6 km/tick is roughly six times the opening haulage, which moves the
 * first bottleneck off the fleet and onto the factory almost immediately —
 * where the interesting decisions are.
 */
export const STARTING_DRONES = 4 as const;

/** Credits the colony lands with. Enough to matter, not enough to skip mining. */
export const STARTING_CREDITS = 2000 as const;

/**
 * Scrap value for an element no recipe consumes, or one drawn from the air.
 *
 * Rarity without demand is not value. Pricing on scarcity alone made fluorine
 * the most valuable element in the game at 2,396 a unit, purely because apatite
 * yields one per unit — and nothing anywhere consumes fluorine. Waste should be
 * something to clear at a modest steady return, not the best income available.
 */
export const WASTE_PRICE = 4 as const;

// --- Factory assembly bay -----------------------------------------------------
//
// The main factory builds drones and mechs itself, from goods. Not instantly:
// there is one bay, it works one order at a time, and the queue behind it is a
// real cost. That is what stops "buy throughput" being a button and makes early
// fleet decisions bind — a mech ordered now is a mech not arriving for forty
// ticks, and a heavy drone costs twice that.
//
// Factory districts do NOT speed this up. They raise component throughput only.
// Assembling finished machines is the main factory's own work, and the larger
// manufacturing plants that will take it over are a later structure.

/** Ticks to assemble one mech. */
export const ASSEMBLY_TICKS_MECH = 40 as const;

/** Ticks to assemble one drone, by class. Heavies take twice a standard. */
export const ASSEMBLY_TICKS_DRONE_STANDARD = 30 as const;
export const ASSEMBLY_TICKS_DRONE_HEAVY = 60 as const;

/** Orders that can wait behind the one in the bay. */
export const ASSEMBLY_QUEUE_LIMIT = 8 as const;
